import { z } from "zod";
import { AgentId, Ok, TemplateId, TenantId } from "./common.js";
import { AgentCapabilityOverrides, CapabilityId, CapabilityRef, Template } from "./entities.js";
import { Capability, CapabilityManifest, EffectiveCapabilities, McpServer, McpServerId, MediaProviderKey, Secret, SecretId } from "./capability.js";
import { Model3dOp } from "./collab.js";
import { r, type RouteDef } from "./route-def.js";

/**
 * P7 能力包（doc/capabilities.md）：能力库、模板 / agent 的能力组合、租户密钥、外部 MCP 服务、自带媒体供应商 key。
 */
const TidParams = z.object({ tid: TenantId });
const CapParams = z.object({ capid: CapabilityId });

export const capabilityRoutes = {
  // ── 能力库
  capabilityList: r({
    phase: 7, method: "GET", path: "/capabilities", summary: "能力库（内置 + 本租户）", tags: ["capability"], auth: "user",
    query: z.object({ tenantId: TenantId.optional(), category: z.string().optional(), q: z.string().optional() }),
    response: z.object({ items: z.array(Capability) }),
  }),
  capabilityGet: r({
    phase: 7, method: "GET", path: "/capabilities/:capid", summary: "能力包详情（含 skill 文件）；tenantId 指定时优先取该租户的同名能力包", tags: ["capability"], auth: "user",
    params: CapParams, query: z.object({ tenantId: TenantId.optional(), version: z.coerce.number().int().positive().optional() }),
    response: Capability, errors: ["capability.not_found"],
  }),
  capabilityVersions: r({
    phase: 7, method: "GET", path: "/capabilities/:capid/versions", summary: "版本历史", tags: ["capability"], auth: "user",
    params: CapParams, query: z.object({ tenantId: TenantId.optional() }),
    response: z.object({ items: z.array(Capability.pick({ id: true, version: true, createdAt: true, status: true })) }),
  }),
  capabilityCreate: r({
    phase: 7, method: "POST", path: "/tenants/:tid/capabilities", summary: "新建租户能力包（不能与内置 id 重名）", tags: ["capability"], auth: "user", idempotent: true,
    permission: "capability.manage", params: TidParams, body: z.object({ id: CapabilityId, manifest: CapabilityManifest }),
    response: Capability, errors: ["capability.id_taken", "capability.invalid"],
  }),
  capabilityUpdate: r({
    phase: 7, method: "PUT", path: "/tenants/:tid/capabilities/:capid", summary: "发布新版本（版本号 +1；引用固定版本的模板不受影响）", tags: ["capability"], auth: "user",
    permission: "capability.manage", params: TidParams.extend({ capid: CapabilityId }), body: z.object({ manifest: CapabilityManifest }),
    response: Capability, errors: ["capability.not_found", "capability.invalid"],
  }),
  capabilityDeprecate: r({
    phase: 7, method: "DELETE", path: "/tenants/:tid/capabilities/:capid", summary: "下线（标记 deprecated；已引用的模板照常可用）", tags: ["capability"], auth: "user",
    permission: "capability.manage", params: TidParams.extend({ capid: CapabilityId }), response: Ok, errors: ["capability.not_found"],
  }),

  // ── 模板 / agent 的能力组合
  templateCapabilitiesPut: r({
    phase: 7, method: "PUT", path: "/templates/:tplid/capabilities", summary: "设置模板引用的能力包（模板版本 +1）", tags: ["capability"], auth: "user",
    permission: "project.write", params: z.object({ tplid: TemplateId }), body: z.object({ capabilities: z.array(CapabilityRef) }),
    response: Template, errors: ["capability.not_found", "capability.image_conflict"],
  }),
  agentCapabilitiesGet: r({
    phase: 7, method: "GET", path: "/agents/:aid/capabilities", summary: "agent 的有效能力（模板 ± 实例增减，解析后）", tags: ["capability"], auth: "user",
    permission: "agent.read", params: z.object({ aid: AgentId }), response: EffectiveCapabilities,
  }),
  agentCapabilitiesPut: r({
    phase: 7, method: "PUT", path: "/agents/:aid/capabilities", summary: "设置实例级能力增减并重新配置 agent", tags: ["capability"], auth: "user",
    permission: "agent.control", params: z.object({ aid: AgentId }), body: AgentCapabilityOverrides,
    response: EffectiveCapabilities, errors: ["capability.not_found", "capability.image_conflict"],
  }),

  // ── 租户密钥（值永不回传）
  secretList: r({
    phase: 7, method: "GET", path: "/tenants/:tid/secrets", summary: "密钥列表（只有名字与 hint）", tags: ["secret"], auth: "user",
    permission: "tenant.read", params: TidParams, response: z.object({ items: z.array(Secret) }),
  }),
  secretPut: r({
    phase: 7, method: "PUT", path: "/tenants/:tid/secrets/:name", summary: "新建或覆盖密钥", tags: ["secret"], auth: "user",
    permission: "secret.manage", params: TidParams.extend({ name: Secret.shape.name }),
    body: z.object({ value: z.string().min(1).max(8192), description: z.string().max(500).optional() }), response: Secret,
  }),
  secretDelete: r({
    phase: 7, method: "DELETE", path: "/tenants/:tid/secrets/:name", summary: "删除密钥（被引用时拒绝）", tags: ["secret"], auth: "user",
    permission: "secret.manage", params: TidParams.extend({ name: Secret.shape.name }), response: Ok, errors: ["secret.in_use", "secret.not_found"],
  }),

  // ── 外部 MCP 服务
  mcpServerList: r({
    phase: 7, method: "GET", path: "/tenants/:tid/mcp-servers", summary: "外部 MCP 服务", tags: ["mcp-server"], auth: "user",
    permission: "tenant.read", params: TidParams, response: z.object({ items: z.array(McpServer) }),
  }),
  mcpServerCreate: r({
    phase: 7, method: "POST", path: "/tenants/:tid/mcp-servers", summary: "登记外部 MCP 服务（请求头可引用 {{secret:NAME}}）", tags: ["mcp-server"], auth: "user", idempotent: true,
    permission: "secret.manage", params: TidParams, body: McpServer.pick({ name: true, url: true, headers: true }), response: McpServer, errors: ["secret.not_found"],
  }),
  mcpServerPatch: r({
    phase: 7, method: "PATCH", path: "/mcp-servers/:msid", summary: "修改", tags: ["mcp-server"], auth: "user",
    permission: "secret.manage", params: z.object({ msid: McpServerId }), body: McpServer.pick({ name: true, url: true, headers: true }).partial(), response: McpServer,
  }),
  mcpServerDelete: r({
    phase: 7, method: "DELETE", path: "/mcp-servers/:msid", summary: "删除（被能力包引用时拒绝）", tags: ["mcp-server"], auth: "user",
    permission: "secret.manage", params: z.object({ msid: McpServerId }), response: Ok, errors: ["mcp.server_in_use"],
  }),
  mcpServerProbe: r({
    phase: 7, method: "POST", path: "/mcp-servers/:msid/probe", summary: "探测：经代理连接并列出工具", tags: ["mcp-server"], auth: "user",
    permission: "secret.manage", params: z.object({ msid: McpServerId }), response: McpServer, errors: ["mcp.server_unreachable"],
  }),

  // ── 媒体供应商
  mediaProviderList: r({
    phase: 7, method: "GET", path: "/media/providers", summary: "可用的媒体供应商（平台是否提供 key）", tags: ["media"], auth: "user",
    response: z.object({ items: z.array(z.object({ id: z.string(), kinds: z.array(z.enum(["image", "model3d"])), platformKey: z.boolean(), ops: z.array(Model3dOp).optional() })) }),
  }),
  mediaKeyList: r({
    phase: 7, method: "GET", path: "/tenants/:tid/media-keys", summary: "租户自带的供应商 key（引用密钥）", tags: ["media"], auth: "user",
    permission: "tenant.read", params: TidParams, response: z.object({ items: z.array(MediaProviderKey) }),
  }),
  mediaKeyPut: r({
    phase: 7, method: "PUT", path: "/tenants/:tid/media-keys/:provider", summary: "设置自带 key（启用后该供应商走租户 key，只计量不扣费）", tags: ["media"], auth: "user",
    permission: "secret.manage", params: TidParams.extend({ provider: z.string().min(1).max(64) }), body: z.object({ secretId: SecretId, enabled: z.boolean().default(true) }),
    response: MediaProviderKey, errors: ["secret.not_found", "media.provider_unavailable"],
  }),
  mediaKeyDelete: r({
    phase: 7, method: "DELETE", path: "/tenants/:tid/media-keys/:provider", summary: "删除自带 key（回到平台 key 计费）", tags: ["media"], auth: "user",
    permission: "secret.manage", params: TidParams.extend({ provider: z.string().min(1).max(64) }), response: Ok,
  }),
} as const satisfies Record<string, RouteDef>;
